import {Component, OnDestroy, OnInit} from '@angular/core';
import {CollectionsService} from '../shared/services/collections.service';
import {Subscription} from 'rxjs';
import {CollectionModel} from '../shared/models/collection.model';
import {AuthService} from "../shared/services/auth.service";
import {
  CatalogSourceFilter,
  readCatalogSourceFilter,
  writeCatalogSourceFilter,
} from "../shared/utils/catalog-source-filter.storage";

@Component({
  selector: 'app-product-catalog',
  templateUrl: './product-catalog.component.html',
  styleUrls: ['./product-catalog.component.scss']
})
export class ProductCatalogComponent implements OnInit, OnDestroy {
  loading: boolean = true;
  searchText: string = '';
  sourceFilter: CatalogSourceFilter = 'all';

  collectionsSubscription: Subscription = new Subscription();
  collections: CollectionModel[] = [];
  filteredCollections: CollectionModel[] = [];
  userSubscription: Subscription;
  isLoggedIn: boolean;

  constructor(
    private authService: AuthService,
    private collectionsService: CollectionsService,
  ) {
  }

  ngOnInit(): void {
    // Restore the source filter chosen earlier this session so leaving the
    // catalogue (e.g. to a product page) and returning keeps the selection.
    this.sourceFilter = readCatalogSourceFilter();
    this.subscribeToUserSubject();
    // Load once on init rather than from the userSubject callback, which previously left the
    // catalog empty until a user emission arrived. The collections endpoint is login-only as
    // of #34, but the route guard has already verified the session with the server before this
    // component is instantiated, so no request is issued for a stale session.
    this.getCollections();
  }

  private subscribeToUserSubject(): void {
    this.userSubscription = this.authService.userSubject.subscribe(user => {
      this.isLoggedIn = !!user;
    })
  }

  private getCollections(): void {
    this.collectionsSubscription = this.collectionsService.getCollections().subscribe({
      next: (res) => {
        this.collections = res;
        // Apply the (possibly restored) source filter to the fresh data rather
        // than showing everything, so a restored selection takes effect on load.
        this.filterCollections();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching collections:', error);
        this.loading = false;
      }
    });
  }

  /** Persist and apply a source-filter change (search text is intentionally not persisted). */
  onSourceFilterChange(): void {
    writeCatalogSourceFilter(this.sourceFilter);
    this.filterCollections();
  }

  filterCollections(): void {
    const search = this.searchText.toLowerCase().trim();

    this.filteredCollections = this.collections.filter(c => {
      const matchesSearch =
        c.public_title?.toLowerCase().includes(search) || c.title.toLowerCase().includes(search);

      const matchesSource =
        this.sourceFilter === 'all' ||
        (this.sourceFilter === 'med' && c.is_med) ||
        (this.sourceFilter === 'iolr' && !c.is_med);

      return matchesSearch && matchesSource;
    });
  }

  ngOnDestroy(): void {
    this.collectionsSubscription.unsubscribe();
    this.userSubscription?.unsubscribe();
  }
}
